// engine-exception: dashboards compose KPI reads (/v1/kpis/:name — value, previous, 12-month series, bands) into tiles per audience, or a connected system's own dashboards and reports (SourceReports.tsx); neither is a table with meta, so no ScreenDef kind renders them
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiQuery } from '../../engine/api/hooks';
import { Badge, EmptyState, ErrorBanner, PageHeader, Skeleton, Stat, type Tone } from '../../engine/design/primitives/Misc';
import { Tabs } from '../../engine/design/primitives/Tabs';
import { CategoryBars, Delta, Sparkline } from '../../engine/design/primitives/Viz';
import { formatMinutes, formatMoney, toMinorInt } from '../../lib/format';
import { systemName, useDataSource } from '../../engine/meta/useDataSource';
import { SourceDashboards } from './SourceReports';

// Dashboards (docs/rtos-adoption-plan.md §5): Executive, Service, People and Client profitability.
// Each KPI tile shows the last 30 days against the 30 before (▴/▾ toned by which direction is
// good), a 12-month sparkline and the benchmark band; money arrives as minor-unit strings and is
// formatted, never converted to floats here.

export type KpiUnit = 'money' | 'percent' | 'minutes' | 'score';
export interface KpiDef { name: string; label: string; unit: KpiUnit; direction: 'up' | 'down'; bands: { good: number; warn: number } | null; note: string; dashboards: string[] }
export interface Kpi extends KpiDef {
  currency: string | null;
  as_of: string;
  value: string | number | null;
  previous: string | number | null;
  series: { period: string; value: string | number | null; partial?: boolean }[];
  breakdown?: { key: string; label: string; value: number | null }[];
}

const DASHBOARDS = [
  { key: 'executive', label: 'Executive' },
  { key: 'service', label: 'Service' },
  { key: 'people', label: 'People' },
  { key: 'clients', label: 'Client profitability' },
];

/** A KPI value for display, by unit. */
export function formatKpi(unit: KpiUnit, v: string | number | null, currency: string | null): string {
  if (v === null) return '—';
  if (unit === 'money') return formatMoney(v, currency ?? 'USD');
  if (unit === 'percent') return `${Number(v).toFixed(1)}%`;
  if (unit === 'minutes') return formatMinutes(v);
  return Number(v).toFixed(2);
}

/** Numeric form for comparisons and the sparkline (money through toMinorInt). */
const numeric = (unit: KpiUnit, v: string | number | null): number | null => (v === null ? null : unit === 'money' ? toMinorInt(v) : Number(v));

/** good / ok / poor against the benchmark bands, in the metric's direction. */
export function bandOf(k: Pick<KpiDef, 'bands' | 'direction'>, v: number | null): { tone: Tone; label: string } | null {
  if (!k.bands || v === null) return null;
  const better = (a: number, b: number) => (k.direction === 'up' ? a >= b : a <= b);
  if (better(v, k.bands.good)) return { tone: 'pos', label: 'Good' };
  if (better(v, k.bands.warn)) return { tone: 'warn', label: 'Fair' };
  return { tone: 'neg', label: 'Poor' };
}

function KpiTile({ name }: { name: string }) {
  const q = useApiQuery<Kpi>(['kpis', name], `/v1/kpis/${name}`, { staleTime: 300_000 });
  const k = q.data;
  if (q.isLoading) return <Skeleton lines={3} />;
  if (q.error || !k) return <ErrorBanner error={q.error} />;
  const value = numeric(k.unit, k.value);
  const prev = numeric(k.unit, k.previous);
  const change = value !== null && prev !== null ? value - prev : null;
  const band = bandOf(k, value);
  const series = k.series.map((p) => numeric(k.unit, p.value));
  return (
    <div className="hf-kpi" role="listitem">
      <Stat
        label={k.label}
        value={formatKpi(k.unit, k.value, k.currency)}
        delta={
          change === null ? (
            <span className="hf-muted">No earlier period to compare</span>
          ) : (
            <Delta value={change} good={k.direction}>
              {formatKpi(k.unit, k.unit === 'money' ? String(Math.abs(change)) : Math.abs(change), k.currency)} vs previous 30 days
            </Delta>
          )
        }
        visual={<Sparkline values={series} label={`${k.label}, last 12 months`} />}
        foot={
          <span className="hf-row" style={{ gap: 'var(--s-2)' }}>
            {band && <Badge tone={band.tone}>{band.label}</Badge>}
            <span className="hf-muted">{k.note}</span>
          </span>
        }
      />
      {k.breakdown && k.breakdown.length > 0 && (
        <CategoryBars items={k.breakdown.map((b) => ({ key: b.key, label: b.label, value: b.value ?? 0 }))} format={(n) => formatMinutes(n)} />
      )}
    </div>
  );
}

function ClientProfitability() {
  const q = useApiQuery<{ data: { client_id: string; name: string; margin_minor: string }[] }>(['reports', 'client-profitability'], '/v1/reports/client-profitability');
  const mrr = useApiQuery<Kpi>(['kpis', 'mrr'], '/v1/kpis/mrr', { staleTime: 300_000 });
  const currency = mrr.data?.currency ?? 'USD';
  const rows = useMemo(() => q.data?.data ?? [], [q.data]);
  if (q.isLoading) return <Skeleton lines={6} />;
  if (q.error) return <ErrorBanner error={q.error} />;
  if (rows.length === 0) return <EmptyState title="No client profitability yet" hint="Invoices and logged time build it." icon="bar-chart" />;
  const sorted = [...rows].sort((a, b) => toMinorInt(b.margin_minor) - toMinorInt(a.margin_minor));
  const best = sorted.filter((r) => toMinorInt(r.margin_minor) > 0).slice(0, 5);
  const worst = sorted.filter((r) => toMinorInt(r.margin_minor) < 0).slice(-5).reverse();
  const bars = (list: typeof rows, tone: 'pos' | 'neg') => (
    <CategoryBars items={list.map((r) => ({ key: r.client_id, label: r.name, value: Math.abs(toMinorInt(r.margin_minor)), tone }))} format={(n) => formatMoney(tone === 'neg' ? -n : n, currency)} />
  );
  return (
    <div className="hf-stack">
      <section aria-label="Most profitable clients" className="hf-stack" style={{ gap: 'var(--s-2)' }}>
        <h2 className="hf-section-title"><span>Most profitable</span></h2>
        {best.length ? bars(best, 'pos') : <p className="hf-muted">No client has a positive margin yet.</p>}
      </section>
      <section aria-label="Loss-making clients" className="hf-stack" style={{ gap: 'var(--s-2)' }}>
        <h2 className="hf-section-title"><span>Loss-making</span></h2>
        {worst.length ? bars(worst, 'neg') : <p className="hf-muted">No client is below cost.</p>}
      </section>
      <Link to="/reports?report=client-profitability" className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>Open the full client profitability report →</Link>
    </div>
  );
}

export function DashboardsScreen() {
  const { overlay } = useDataSource();
  if (overlay === undefined) return <div className="hf-page"><Skeleton lines={6} /></div>;
  // On a connected system of record its dashboards and reports are the dashboards; NovaPSA's KPIs
  // are computed from tables that tenant does not use.
  if (overlay) return <SourceDashboards system={systemName(overlay)} />;
  return <KpiDashboards />;
}

function KpiDashboards() {
  const [sp, setSp] = useSearchParams();
  const active = DASHBOARDS.find((d) => d.key === sp.get('tab')) ?? DASHBOARDS[0]!;
  const catalog = useApiQuery<{ data: KpiDef[] }>(['kpis'], '/v1/kpis', { staleTime: 3_600_000 });
  const kpis = (catalog.data?.data ?? []).filter((k) => k.dashboards.includes(active.key));
  return (
    <div className="hf-page">
      <PageHeader title="Dashboards" subtitle="The last 30 days against the 30 before, with a year of history" />
      <Tabs
        variant="pills"
        aria-label="Dashboard"
        value={active.key}
        onChange={(key) => setSp((p) => { const n = new URLSearchParams(p); n.set('tab', key); return n; }, { replace: true })}
        tabs={DASHBOARDS.map((d) => ({ key: d.key, label: d.label }))}
      />
      <div className="hf-tabpanel" role="tabpanel" aria-label={active.label}>
        {active.key === 'clients' ? (
          <ClientProfitability />
        ) : catalog.isLoading ? (
          <Skeleton lines={6} />
        ) : catalog.error ? (
          <ErrorBanner error={catalog.error} />
        ) : (
          <div className="hf-kpis" role="list" aria-label={`${active.label} KPIs`}>
            {kpis.map((k) => (
              <KpiTile key={k.name} name={k.name} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
